import React from "react";
import { motion } from "framer-motion";

const MANIFESTO = [
  { label: "Clear processes", body: "Written down before they're needed." },
  { label: "Clear ownership", body: "Every step has a name against it." },
  { label: "Useful documentation", body: "Short enough to read in five minutes." },
  { label: "Accessible systems", body: "Anyone in the team can pick them up." },
  { label: "Supported people", body: "Training, context and safety to run it." },
];

export default function BuiltToRun() {
  return (
    <section
      className="bg-[#0B0B0C] border-t border-[#343437] relative overflow-hidden grain"
      data-testid="home-built-to-run"
      aria-labelledby="home-built-to-run-title"
    >
      <div
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{
          background:
            "radial-gradient(1100px 500px at 8% 100%, rgba(216,169,54,0.10), transparent 60%)",
        }}
        aria-hidden
      />
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          {/* Left: kicker + editorial statement + signature */}
          <div className="lg:col-span-8">
            <p className="eyebrow">My approach</p>
            <motion.h2
              id="home-built-to-run-title"
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8 }}
              className="mt-4 font-display leading-[0.94] tracking-[-0.03em] text-[#FAF9F6]"
              style={{ fontSize: "clamp(3rem, 7vw, 6.5rem)" }}
            >
              Built to run <br />
              without <span className="text-[#D8A936]">me.</span>
            </motion.h2>

            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 1, delay: 0.35 }}
              className="mt-6 pl-1"
              aria-hidden
            >
              <span
                className="font-signature text-3xl md:text-4xl text-[#D8A936]/85 inline-block -rotate-[3deg]"
                style={{ letterSpacing: "-0.01em" }}
              >
                — Built to run without me.
              </span>
            </motion.div>

            <p className="mt-9 font-display text-lg md:text-xl text-[#FAF9F6]/90 leading-snug max-w-2xl">
              Good systems shouldn't depend on the person who created them.
            </p>

            <div className="mt-5 space-y-4 text-[#A9A9A6] leading-relaxed text-[15px] max-w-2xl">
              <p>
                The best measure of a leader is not how essential they are in the room — it's how quickly the room stops needing them.
              </p>
              <p>
                Sustainable transformation isn't a launch event. It's what still runs on day 300 — because the process is clear, the ownership is named, the documentation is useful, the systems are accessible, and the people using them are supported.
              </p>
              <p>
                Every process, team structure and piece of technology I put in place is designed for that day. So that when I move on, the work doesn't leave with me.
              </p>
            </div>
          </div>

          {/* Right: five-line manifesto rail */}
          <div className="lg:col-span-4 lg:pt-14">
            <p className="eyebrow mb-6">What that looks like</p>
            <ul className="space-y-6">
              {MANIFESTO.map((m, i) => (
                <motion.li
                  key={m.label}
                  initial={{ opacity: 0, x: 12 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.55, delay: 0.15 + i * 0.06 }}
                  className="relative pl-5 border-l border-[#343437] hover:border-[#D8A936] transition-colors duration-500"
                >
                  <p className="font-display text-[12px] tracking-[0.22em] uppercase text-[#D8A936]">
                    {m.label}
                  </p>
                  <p className="mt-1.5 text-[#A9A9A6] leading-relaxed text-[14px]">
                    {m.body}
                  </p>
                </motion.li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
